import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import {
  useCreatePurchaseOrder,
  usePurchaseOrderDetail,
  useSuppliers,
  useProducts,
} from '@hooks/useProcurement'

const poItemSchema = z.object({
  productId: z.coerce.number().positive('Product is required'),
  qtyOrdered: z.coerce.number().positive('Quantity must be greater than 0'),
  uom: z.string().min(1, 'UOM is required'),
  expectedRate: z.coerce.number().positive().optional(),
})

const poSchema = z.object({
  supplierId: z.coerce.number().positive('Supplier is required'),
  branchId: z.coerce.number().positive('Branch is required'),
  orderDate: z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date'),
  expectedDeliveryDate: z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date'),
  items: z.array(poItemSchema).min(1, 'At least one item is required'),
})

type POFormData = z.infer<typeof poSchema>

export function PurchaseOrderFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [error, setError] = useState<string | null>(null)

  const { data: poData } = usePurchaseOrderDetail(id ? parseInt(id) : 0)
  const { data: suppliersData } = useSuppliers()
  const { data: productsData } = useProducts()
  const create = useCreatePurchaseOrder()

  const suppliers = suppliersData?.data?.items || []
  const products = productsData?.data?.items || []

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<POFormData>({
    defaultValues: {
      supplierId: 0,
      branchId: 1, // Default to first branch
      orderDate: new Date().toISOString().split('T')[0],
      expectedDeliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      items: [{ productId: 0, qtyOrdered: 0, uom: 'KG', expectedRate: 0 }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  })

  const selectedItems = watch('items')

  const onSubmit = async (data: POFormData) => {
    try {
      setError(null)
      const formattedData = {
        ...data,
        orderDate: new Date(data.orderDate).toISOString(),
        expectedDeliveryDate: new Date(data.expectedDeliveryDate).toISOString(),
        items: data.items.map((item) => ({
          ...item,
          expectedRate: item.expectedRate || undefined,
        })),
      }

      await create.mutateAsync(formattedData)
      navigate('/dashboard/procurement/purchase-orders')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create purchase order')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Edit' : 'Create'} Purchase Order
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Place an order with a supplier
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/procurement/purchase-orders')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">PO Header</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier *
              </label>
              <select {...register('supplierId')} className="w-full">
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {errors.supplierId && (
                <span className="text-red-500 text-sm">
                  {errors.supplierId.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <select {...register('branchId')} className="w-full">
                <option value="1">Main Repacking Unit</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Date *
              </label>
              <input
                {...register('orderDate')}
                type="date"
                className="w-full"
              />
              {errors.orderDate && (
                <span className="text-red-500 text-sm">
                  {errors.orderDate.message}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Delivery Date *
              </label>
              <input
                {...register('expectedDeliveryDate')}
                type="date"
                className="w-full"
              />
              {errors.expectedDeliveryDate && (
                <span className="text-red-500 text-sm">
                  {errors.expectedDeliveryDate.message}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Line Items</h2>
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-semibold">Product</th>
                  <th className="text-left px-4 py-2 font-semibold">UOM</th>
                  <th className="text-right px-4 py-2 font-semibold">Qty Ordered</th>
                  <th className="text-right px-4 py-2 font-semibold">Expected Rate</th>
                  <th className="text-center px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <select
                        {...register(`items.${index}.productId`, {
                          valueAsNumber: true,
                        })}
                        className="w-full text-sm"
                      >
                        <option value="">Select Product</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                      {errors.items?.[index]?.productId && (
                        <span className="text-red-500 text-xs">
                          {errors.items[index].productId?.message}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <input
                        {...register(`items.${index}.uom`)}
                        type="text"
                        className="w-24 text-sm"
                        disabled
                        value={
                          selectedItems[index]?.productId
                            ? products.find(
                                (p) => p.id === selectedItems[index].productId
                              )?.baseUom || 'KG'
                            : 'KG'
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        {...register(`items.${index}.qtyOrdered`, {
                          valueAsNumber: true,
                        })}
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-24 text-right text-sm"
                      />
                      {errors.items?.[index]?.qtyOrdered && (
                        <span className="text-red-500 text-xs">
                          {errors.items[index].qtyOrdered?.message}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <input
                        {...register(`items.${index}.expectedRate`, {
                          valueAsNumber: true,
                        })}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Optional"
                        className="w-28 text-right text-sm"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() =>
              append({ productId: 0, qtyOrdered: 0, uom: 'KG', expectedRate: 0 })
            }
            className="btn-secondary text-sm"
          >
            + Add Item
          </button>

          {errors.items && (
            <span className="text-red-500 text-sm block mt-2">
              {errors.items.message}
            </span>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/procurement/purchase-orders')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Purchase Order'}
          </button>
        </div>
      </form>
    </div>
  )
}
